/**
 * @fileoverview Canonical new-case form fields, values, validation, and related-entity taxonomy.
 * @module core-domain/forms/caseFormSchema
 *
 * Responsibilities:
 * - Define ordered form fields, NewSeriesFormValues, and Zod validation schemas.
 * - Define CASE_TYPE_DEFINITIONS, linked-entity filters, and inspection subtypes.
 * - Keep case-type dropdown options lookup-driven while retaining taxonomy helpers
 *   for related-entity filtering and subtype metadata.
 *
 * Dependencies: Zod and shared form field definitions.
 * Architecture: Pure package-owned contracts consumed by host forms and local-first builders.
 * Notes: Validation is deterministic and contains no persistence or network behavior.
 *        UI hosts load case-type options from Dexie lookups; getAvailableCaseTypes
 *        remains available for related-entity filtering.
 */
import { z } from 'zod';
import type { FormFieldDef } from './shared/fieldTypes';

// ---- Field Definitions (ordered) ----

/** @contract NEW_SERIES_FIELDS — ordered fields, rules, sync: pure form metadata. */
export const NEW_SERIES_FIELDS: FormFieldDef[] = [
  { key: 'entity',               labelKey: 'entity',            type: 'entity-search', required: true,  quickStart: true,  order: 1, span: 2 },
  { key: 'caseTypeCode',       labelKey: 'caseType',        type: 'select',        required: true,  quickStart: true,  order: 2, span: 1 },
  { key: 'templateName',         labelKey: 'template',          type: 'select',        required: true,  quickStart: true,  order: 3, span: 1, dependsOn: 'caseTypeCode' },
  { key: 'inspectionTypeCode',   labelKey: 'inspectionType',    type: 'select',        required: true,  quickStart: true,  order: 4, span: 1 },
  { key: 'inspectionAccountName', labelKey: 'inspectionAccount', type: 'select',       required: true,  quickStart: true,  order: 4, span: 1 },
  { key: 'fiscalYear',           labelKey: 'fiscalYear',        type: 'number',        required: true,  quickStart: true,  order: 5, span: 1 },
  { key: 'scheduledDate',        labelKey: 'scheduledDate',     type: 'date',          required: true,  quickStart: true,  order: 6, span: 1 },
  { key: 'notes',                labelKey: 'notes',             type: 'textarea',      required: false, quickStart: false, order: 7, span: 2 },
];

/** @contract QUICK_START_FIELDS — quick-start field projection; pure and deterministic. */
export const QUICK_START_FIELDS = NEW_SERIES_FIELDS.filter(f => f.quickStart);
/** @contract ADVANCED_ONLY_FIELDS — advanced-only field projection; pure and deterministic. */
export const ADVANCED_ONLY_FIELDS = NEW_SERIES_FIELDS.filter(f => !f.quickStart);

// ---- Form Data Shape ----

/** @contract LinkedEntity — linked entity identity/category fields; lookup values are caller-resolved. */
export interface LinkedEntity {
  entityId: string;
  entityName: string;
  entityAddress: string;
  entityCategory: 'parcel' | 'residential' | 'building' | 'unit' | 'site' | 'complex' | 'hydrant';
  /**
   * structure    — a building/residential structure on a parcel (defensible space, AB38, fire code)
   * parcel       — the land parcel under a site or complex
   * unit         — individual tenant space within a building or complex
   * outbuilding  — shed, detached garage, pool building, etc.
   * related      — generic relationship (e.g. parcel linked to a fire code building inspection)
   */
  role?: 'structure' | 'parcel' | 'unit' | 'outbuilding' | 'related';
}

/** @contract NewSeriesFormValues — complete case-form values; rules are enforced by schemas. */
export interface NewSeriesFormValues {
  entityId: string;
  entityName: string;
  entityCategory: string;
  entityAddress: string;
  inspectionAccountId: string;
  inspectionAccountName: string;
  templateId: string;
  templateName: string;
  caseTypeCode: string;
  inspectionTypeCode: string;
  fiscalYear: number;
  scheduledDate: string;
  notes: string;
  /** Entities co-inspected in this case (auto-populated from relatedEntities) */
  linkedEntities?: LinkedEntity[];
}

// ---- Validation (Zod) ----

/** @process newSeriesValidationSchema — validate required case fields; failure returns Zod issues; idempotent. */
export const newSeriesValidationSchema = z.object({
  entityId:               z.string().min(1, 'Please select an entity'),
  entityName:             z.string().min(1),
  entityCategory:         z.string().min(1),
  entityAddress:          z.string(),
  inspectionAccountId:    z.string().optional().default(''),
  inspectionAccountName:  z.string().min(1, 'Please select an inspection account'),
  templateId:             z.string().optional().default(''),
  templateName:           z.string().min(1, 'Please select a template'),
  caseTypeCode:         z.string().min(1, 'Please select a series type'),
  inspectionTypeCode:     z.string().min(1, 'Please select an inspection type'),
  fiscalYear:             z.number().int().min(2000).max(2100),
  scheduledDate:          z.string().min(1, 'Please select a scheduled date'),
  notes:                  z.string().optional().default(''),
});

/** @process quickStartValidationSchema — validate quick-start subset; failure returns Zod issues; idempotent. */
export const quickStartValidationSchema = newSeriesValidationSchema.pick({
  entityId: true,
  entityName: true,
  entityCategory: true,
  entityAddress: true,
  inspectionAccountId: true,
  inspectionAccountName: true,
  templateName: true,
  caseTypeCode: true,
  fiscalYear: true,
  scheduledDate: true,
});

// ---- Entity Pre-Selection ----

/** @contract EntityPreSelection — preselected entity identity; pure UI input. */
export interface EntityPreSelection {
  _id: string;
  entityName: string;
  entityCategory: string;
  entityAddress: string;
  inspectionAccountId: string;
  inspectionAccountName: string;
}

// ---- Entity Defaults ----

/** @contract EntityDefaults — derived defaults for case creation; no persistence. */
export interface EntityDefaults {
  defaultInspectionAccountId?: string;
  defaultInspectionAccountName?: string;
  defaultCaseType?: string;
  defaultInspectionType?: string;
  defaultTemplateId?: string;
  defaultTemplateName?: string;
}

// ---- Default Computation ----

/** @process computeSeriesDefaults — derive defaults from entity/lookups; failure uses explicit empty defaults; idempotent. */
export function computeSeriesDefaults(
  entityDefaults?: EntityDefaults | null,
  _entityCategory?: string,
): Partial<NewSeriesFormValues> {
  return {
    inspectionAccountId:   entityDefaults?.defaultInspectionAccountId ?? '',
    inspectionAccountName: entityDefaults?.defaultInspectionAccountName ?? '',
    caseTypeCode:        entityDefaults?.defaultCaseType ?? '',
    templateId:            entityDefaults?.defaultTemplateId ?? '',
    templateName:          entityDefaults?.defaultTemplateName ?? '',
    fiscalYear:            new Date().getFullYear(),
    notes:                 '',
  };
}

// ---- Case Type Taxonomy ----
//
// Used for related-entity auto-link rules and inspection subtypes, NOT for CASE
// TYPE dropdown options. Dropdowns must read Dexie lookup_values (`caseType`).
//
// primaryEntityCategories  — which entity category this case type applies to
// linkedEntityFilter       — which relatedEntity categories are auto-included, and what role they get
//   - For parcel-primary: structures (residential/building) are auto-linked based on filter rules
//   - For building-primary: the parcel may be conditionally linked (fire code with outdoor storage)
//   - For site/complex-primary: all sub-parcels and buildings are included
// inspectionSubtypes       — available inspection sub-types for this case type

/** @contract LinkedEntityFilter — case-type linking rule; pure configuration. */
export interface LinkedEntityFilter {
  entityCategory: 'parcel' | 'residential' | 'building' | 'unit' | 'site' | 'complex' | 'hydrant';
  role: 'structure' | 'parcel' | 'unit' | 'outbuilding' | 'related';
  /** If true, only include if no other structure-role entities exist (conditional) */
  conditional?: boolean;
}

/** @contract CaseTypeDefinition — case type and category/filter metadata; lookup-driven. */
export interface CaseTypeDefinition {
  code: string;
  name: string;
  /** Entity categories that can be the PRIMARY entity for this case type */
  primaryEntityCategories: Array<'parcel' | 'residential' | 'building' | 'unit' | 'site' | 'complex' | 'hydrant'>;
  /** Rules for auto-linking related entities from the primary entity's relatedEntities[] */
  linkedEntityFilters: LinkedEntityFilter[];
  /** Available inspection subtypes for this case type */
  inspectionSubtypes: { code: string; name: string }[];
  description: string;
}

/** @contract CASE_TYPE_DEFINITIONS — canonical case-type metadata; pure and deterministic. */
export const CASE_TYPE_DEFINITIONS: CaseTypeDefinition[] = [
  {
    code: 'wildland',
    name: 'Wildland Inspection',
    description: 'Parcel-primary. Includes any residential or commercial structures on the parcel.',
    primaryEntityCategories: ['parcel'],
    linkedEntityFilters: [
      { entityCategory: 'residential', role: 'structure' },
      { entityCategory: 'building',    role: 'structure' },
    ],
    inspectionSubtypes: [
      { code: 'weed-abatement',    name: 'Weed Abatement' },
      { code: 'defensible-space',  name: 'Defensible Space Inspection' },
    ],
  },
  {
    code: 'ab38',
    name: 'AB38 Wildfire Disclosure',
    description: 'Parcel-primary. Residential properties only (1-4 family, duplex, fourplex). Includes residential structures, detached garages, sheds, and outbuildings on the parcel. Commercial structures excluded.',
    primaryEntityCategories: ['parcel'],
    linkedEntityFilters: [
      { entityCategory: 'residential', role: 'structure' },
      // Outbuildings (sheds, detached garages) modeled as 'building' with outbuilding role
      { entityCategory: 'building',    role: 'outbuilding' },
    ],
    inspectionSubtypes: [
      { code: 'ab38-initial',  name: 'AB38 Initial Assessment' },
      { code: 'ab38-recheck', name: 'AB38 Re-Check' },
    ],
  },
  {
    code: 'fire-code',
    name: 'Fire Code Inspection',
    description: 'Building-primary (or unit-primary for child cases in multi-tenant buildings). The structure is the main inspectable entity. The parcel may be conditionally linked if outdoor storage or property cleanup is required.',
    primaryEntityCategories: ['building', 'residential', 'unit', 'complex'],
    linkedEntityFilters: [
      // Parcel is conditionally linked — only when outdoor/property issues exist
      { entityCategory: 'parcel', role: 'related', conditional: true },
    ],
    inspectionSubtypes: [
      { code: 'annual',       name: 'Annual Inspection' },
      { code: 'complaint',    name: 'Complaint Inspection' },
      { code: 'assembly',     name: 'Assembly' },
      { code: 'commercial',   name: 'Commercial' },
      { code: 'industrial',   name: 'Industrial' },
      { code: 'healthcare',   name: 'Healthcare Facility' },
      { code: 'multi-family', name: 'Multi-Family' },
      { code: 'high-hazard',  name: 'High Hazard' },
      { code: 'storage',      name: 'Storage Facility' },
    ],
  },
  {
    code: 'complex',
    name: 'Complex / Strip Mall Inspection',
    description: 'Complex-primary (strip mall, commercial center). Includes the shell building(s) and all tenant units within. Parcel is implicit.',
    primaryEntityCategories: ['complex'],
    linkedEntityFilters: [
      { entityCategory: 'building',    role: 'structure' },
      { entityCategory: 'residential', role: 'unit' },
      { entityCategory: 'parcel',      role: 'parcel' },
    ],
    inspectionSubtypes: [
      { code: 'annual',    name: 'Annual Inspection' },
      { code: 'complaint', name: 'Complaint Inspection' },
    ],
  },
  {
    code: 'site',
    name: 'Site Inspection',
    description: 'Site-primary (fairgrounds, campus, apartment complex spanning multiple parcels). Includes all sub-parcels and all buildings on the site (residential blocks, office, outbuildings, garages, pool buildings).',
    primaryEntityCategories: ['site'],
    linkedEntityFilters: [
      { entityCategory: 'parcel',      role: 'parcel' },
      { entityCategory: 'building',    role: 'structure' },
      { entityCategory: 'residential', role: 'structure' },
    ],
    inspectionSubtypes: [
      { code: 'annual',       name: 'Annual Inspection' },
      { code: 'complaint',    name: 'Complaint Inspection' },
      { code: 'multi-family', name: 'Multi-Family' },
    ],
  },
  {
    code: 'construction',
    name: 'Construction Inspection',
    description: 'Building or parcel primary. Tracks construction-phase fire inspections.',
    primaryEntityCategories: ['building', 'parcel', 'complex', 'site'],
    linkedEntityFilters: [],
    inspectionSubtypes: [
      { code: 'construction', name: 'Construction Inspection' },
    ],
  },
  {
    code: 'hood',
    name: 'Hood & Duct Inspection',
    description: 'Building-primary. Commercial kitchen hood and duct system inspections.',
    primaryEntityCategories: ['building'],
    linkedEntityFilters: [],
    inspectionSubtypes: [
      { code: 'hood-duct', name: 'Hood & Duct Inspection' },
    ],
  },
  {
    code: 'hydrant',
    name: 'Hydrant Inspection',
    description: 'Hydrant-primary. Flow test and maintenance inspections.',
    primaryEntityCategories: ['hydrant'],
    linkedEntityFilters: [],
    inspectionSubtypes: [
      { code: 'hydrant-flow', name: 'Hydrant Flow Test' },
      { code: 'hydrant-maintenance', name: 'Hydrant Maintenance' },
    ],
  },
  {
    code: 'special-event',
    name: 'Special Event',
    description: 'Site or parcel primary. Temporary event inspections (festivals, markets, concerts).',
    primaryEntityCategories: ['site', 'parcel', 'building'],
    linkedEntityFilters: [],
    inspectionSubtypes: [
      { code: 'special-event', name: 'Special Event Inspection' },
    ],
  },
  {
    code: 'complaint',
    name: 'Complaint Inspection',
    description: 'Any entity type. Initiated by a public complaint or referral.',
    primaryEntityCategories: ['parcel', 'building', 'residential', 'site', 'complex'],
    linkedEntityFilters: [
      { entityCategory: 'residential', role: 'structure' },
      { entityCategory: 'building',    role: 'structure' },
    ],
    inspectionSubtypes: [
      { code: 'complaint', name: 'Complaint Investigation' },
    ],
  },
];

/** Flat list for dropdowns — filtered by primary entity category if provided */
/** @process getAvailableCaseTypes — filter case types by category; no persistence; idempotent. */
export function getAvailableCaseTypes(primaryEntityCategory?: string): { code: string; name: string }[] {
  if (!primaryEntityCategory) return CASE_TYPE_DEFINITIONS.map(({ code, name }) => ({ code, name }));
  return CASE_TYPE_DEFINITIONS
    .filter(ct => ct.primaryEntityCategories.includes(primaryEntityCategory as any))
    .map(({ code, name }) => ({ code, name }));
}

/** Get inspection subtypes for a given case type code */
/** @process getInspectionSubtypes — resolve subtypes for a case type; unknown types return empty; idempotent. */
export function getInspectionSubtypes(caseTypeCode: string): { code: string; name: string }[] {
  return CASE_TYPE_DEFINITIONS.find(ct => ct.code === caseTypeCode)?.inspectionSubtypes ?? [];
}

/** Get the linked entity filter rules for a given case type code */
/** @process getLinkedEntityFilters — resolve linking filters; unknown types return empty; idempotent. */
export function getLinkedEntityFilters(caseTypeCode: string): LinkedEntityFilter[] {
  return CASE_TYPE_DEFINITIONS.find(ct => ct.code === caseTypeCode)?.linkedEntityFilters ?? [];
}

// Backward-compat flat list (used in dropdowns that haven't been migrated yet)
/** @contract SERIES_TYPES — display projection of case types; pure metadata. */
export const SERIES_TYPES = CASE_TYPE_DEFINITIONS.map(({ code, name }) => ({ code, name }));

// Backward-compat flat inspection type list
/** @contract INSPECTION_TYPE_OPTIONS — inspection type display metadata; pure configuration. */
export const INSPECTION_TYPE_OPTIONS: { code: string; name: string }[] = [
  { code: 'weed-abatement',       name: 'Weed Abatement' },
  { code: 'defensible-space',     name: 'Defensible Space Inspection' },
  { code: 'ab38-initial',         name: 'AB38 Initial Assessment' },
  { code: 'ab38-recheck',         name: 'AB38 Re-Check' },
  { code: 'annual',               name: 'Annual Inspection' },
  { code: 'complaint',            name: 'Complaint Inspection' },
  { code: 'assembly',             name: 'Assembly Inspection' },
  { code: 'commercial',           name: 'Commercial Inspection' },
  { code: 'industrial',           name: 'Industrial Inspection' },
  { code: 'healthcare',           name: 'Healthcare Facility Inspection' },
  { code: 'multi-family',         name: 'Multi-Family Inspection' },
  { code: 'high-hazard',          name: 'High Hazard Inspection' },
  { code: 'storage',              name: 'Storage Facility Inspection' },
  { code: 'construction',         name: 'Construction Inspection' },
  { code: 'hood-duct',            name: 'Hood & Duct Inspection' },
  { code: 'hydrant-flow',         name: 'Hydrant Flow Test' },
  { code: 'hydrant-maintenance',  name: 'Hydrant Maintenance' },
  { code: 'special-event',        name: 'Special Event Inspection' },
];

